/**
 * StackDiagram — SVG diagram showing the 3-layer Clarity platform architecture.
 * Layers (top → bottom): Customer Micro-SaaS / Clarity Platform / Onwards Chassis
 * Rethemed for Indigo + Gold palette:
 *   - Layer chrome: near-black bg (#0B1220) with hairline borders
 *   - Clarity Platform layer: indigo (#4F46E5) accent border + fill tints
 *   - Customer product tiles: accent-soft fill with indigo stroke
 *   - "Your product" tile: gold-soft fill with gold stroke (dashed)
 *   - Chassis tiles: surface fills with muted colours (no orange)
 */
export function StackDiagram() {
  return (
    <div className="w-full overflow-x-auto">
      <svg
        role="img"
        aria-label="Three-layer architecture diagram: Customer Micro-SaaS on top, Clarity Platform in the middle, and the Onwards Chassis at the bottom comprising Cloudflare, Supabase, Stripe, Resend, and Anthropic Claude."
        viewBox="0 0 900 540"
        xmlns="http://www.w3.org/2000/svg"
        className="mx-auto max-w-4xl w-full"
        style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
      >
        {/* ── Background — near-black moment panel ── */}
        <rect width="900" height="540" fill="#0B1220" rx="16" />

        {/* ══════════════════════════════════════════
            LAYER 1 — Customer Micro-SaaS (top)
        ══════════════════════════════════════════ */}
        {/* Layer chrome: hairline border on near-black */}
        <rect x="40" y="28" width="820" height="108" rx="10" fill="#111827" stroke="rgba(232,234,242,0.12)" strokeWidth="1.5" />
        <text x="60" y="52" fill="rgba(232,234,242,0.5)" fontSize="10" fontWeight="600" letterSpacing="2" textAnchor="start">
          CUSTOMER MICRO-SAAS
        </text>
        {/* Product tiles — accent-soft fill with indigo stroke */}
        {[
          { x: 60,  label: 'Nexlytics' },
          { x: 200, label: 'Mockingbird' },
          { x: 340, label: 'Pulse Check' },
          { x: 480, label: 'Glide' },
          { x: 600, label: 'Mobipet' },
          { x: 720, label: 'Your product', isYours: true },
        ].map((tile) => (
          <g key={tile.label}>
            <rect
              x={tile.x} y="64" width="120" height="58" rx="7"
              fill={tile.isYours ? 'rgba(245,197,24,0.10)' : 'rgba(79,70,229,0.12)'}
              stroke={tile.isYours ? '#F5C518' : '#4F46E5'}
              strokeWidth="1"
              strokeDasharray={tile.isYours ? '5 3' : '0'}
            />
            <text
              x={tile.x + 60} y="97"
              fill={tile.isYours ? '#F5C518' : '#818CF8'}
              fontSize="11" fontWeight="600" textAnchor="middle"
            >
              {tile.label}
            </text>
          </g>
        ))}

        {/* Connector arrow layer 1 → layer 2 */}
        <line x1="450" y1="136" x2="450" y2="162" stroke="rgba(232,234,242,0.2)" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrow)" />

        {/* ══════════════════════════════════════════
            LAYER 2 — Clarity Platform (middle)
            Indigo accent: border + fills
        ══════════════════════════════════════════ */}
        <rect x="40" y="162" width="820" height="108" rx="10" fill="#111827" stroke="#4F46E5" strokeWidth="1.5" />
        <text x="60" y="186" fill="#818CF8" fontSize="10" fontWeight="600" letterSpacing="2">
          CLARITY PLATFORM
        </text>
        {[
          { x: 60,  label: 'AI-CTO / Advisor' },
          { x: 220, label: 'Tenant Provisioner' },
          { x: 390, label: 'Platform API' },
          { x: 560, label: 'Magic-link Auth' },
          { x: 720, label: 'Audit Trail' },
        ].map((tile) => (
          <g key={tile.label}>
            <rect x={tile.x} y="198" width="148" height="58" rx="7" fill="rgba(79,70,229,0.12)" stroke="#4F46E5" strokeWidth="1" />
            <text x={tile.x + 74} y="231" fill="#818CF8" fontSize="11" fontWeight="600" textAnchor="middle">
              {tile.label}
            </text>
          </g>
        ))}

        {/* Connector arrow layer 2 → layer 3 */}
        <line x1="450" y1="270" x2="450" y2="300" stroke="rgba(232,234,242,0.2)" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#arrow)" />

        {/* ══════════════════════════════════════════
            LAYER 3 — Onwards Chassis (bottom)
        ══════════════════════════════════════════ */}
        <rect x="40" y="300" width="820" height="220" rx="10" fill="#111827" stroke="rgba(232,234,242,0.12)" strokeWidth="1.5" />
        <text x="60" y="324" fill="rgba(232,234,242,0.5)" fontSize="10" fontWeight="600" letterSpacing="2">
          ONWARDS CHASSIS
        </text>

        {/* Row 1 — CF suite — cool slate */}
        {[
          { x: 60,  label: 'CF Pages', sub: 'Static hosting' },
          { x: 232, label: 'CF Workers', sub: 'Edge compute' },
          { x: 404, label: 'Workers AI + Vectorize', sub: 'Embeddings + RAG' },
          { x: 620, label: 'Workers KV', sub: 'Session cache' },
        ].map((tile) => (
          <g key={tile.label}>
            <rect x={tile.x} y="336" width="158" height="62" rx="7" fill="rgba(99,102,241,0.10)" stroke="rgba(99,102,241,0.5)" strokeWidth="1" />
            <text x={tile.x + 79} y="362" fill="#A5B4FC" fontSize="11" fontWeight="600" textAnchor="middle">{tile.label}</text>
            <text x={tile.x + 79} y="380" fill="rgba(232,234,242,0.35)" fontSize="9" textAnchor="middle">{tile.sub}</text>
          </g>
        ))}

        {/* Row 2 — Data + Billing + Comms + AI */}
        {[
          { x: 60,  label: 'Supabase Sydney', sub: 'Postgres + RLS', color: '#34D399' },
          { x: 244, label: 'Stripe', sub: 'Subscriptions + Connect', color: '#818CF8' },
          { x: 440, label: 'Resend', sub: 'Transactional email', color: '#818CF8' },
          { x: 632, label: 'Anthropic Claude', sub: 'Haiku 4.5 + Sonnet 4.6', color: '#C084FC' },
        ].map((tile) => (
          <g key={tile.label}>
            <rect
              x={tile.x} y="418" width="178" height="62" rx="7"
              fill={`${tile.color}18`}
              stroke={tile.color}
              strokeWidth="1"
            />
            <text x={tile.x + 89} y="444" fill={tile.color} fontSize="11" fontWeight="600" textAnchor="middle">{tile.label}</text>
            <text x={tile.x + 89} y="462" fill="rgba(232,234,242,0.35)" fontSize="9" textAnchor="middle">{tile.sub}</text>
          </g>
        ))}

        {/* Region badge */}
        <rect x="60" y="494" width="130" height="20" rx="4" fill="rgba(52,211,153,0.08)" stroke="#34D399" strokeWidth="1" />
        <text x="125" y="508" fill="#34D399" fontSize="9" fontWeight="500" textAnchor="middle">
          AU data stays in-country
        </text>

        {/* Arrowhead marker */}
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="4" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="rgba(232,234,242,0.3)" />
          </marker>
        </defs>
      </svg>
    </div>
  );
}
